import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  DocumentSvg,
  EditIcon,
  EditPenSvg,
  SearchIcon,
} from "assets/images/common/svgs";
import { siteRoutes } from "utils/helpers/enums/routes.enum";
import usePolicies from "../useHooks";
import { useDebounce } from "hooks/useDebounce";
import { TeamMemberFilterDTO } from "utils/helpers/models/team-management/team-member-filter.dto";
import { PolicyFilterDTO } from "utils/helpers/models/privacy-policy/policy-filter.dto";
import useStore from "hooks/useStore";
import { sitePermissions } from "utils/helpers/enums/permissions.enum";

const PolicyAgreement: React.FC = () => {
  const { hasPermission } = useStore();
  const navigate = useNavigate();
  const { getPolicies } = usePolicies();
  const [data, setData] = useState<any[]>([]);
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [filters, setFilters] = useState<PolicyFilterDTO>(
    new PolicyFilterDTO()
  );

  const [totalElements, setTotalElements] = useState<number>(0);

  const debounceSearch = useDebounce(filters.heading, 1000);

  const columnsAchievement: string[] = ["Title", "Description", "Action"];

  const onPageChange = (pageInfo: { selected: number; limit: number }) => {
    const { selected: page, limit } = pageInfo;
    filters.size = limit;
    filters.page = page + 1;
    setFilters({ ...filters });
    getAllPolicies(filters);
  };

  const getAllPolicies = (filters: PolicyFilterDTO) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
      page: filters.page - 1,
    };
    getPolicies(setData, queryParams, setFilters, setTotalElements);
  };

  useEffect(() => {
    getAllPolicies(filters);
  }, [debounceSearch]);

  const handleChangeFilter = (event: any) => {
    const {
      target: { name, value },
    } = event;
    setFilters({ ...filters, [name]: value });
    if (name === "nameOrEmail" && value === "") {
      getAllPolicies(filters);
    }
  };

  const resetFilters = () => {
    const model = new TeamMemberFilterDTO();
    for (let key in model) {
      filters[key] = model[key];
    }

    setFilters({ ...filters });
    getAllPolicies(filters);
  };

  const handleSearch = (event: any) => {
    const { key } = event;
    if (key === "Enter") {
      getAllPolicies(filters);
    }
  };

  const goToDetail = (userId: number) => {
    navigate(`${siteRoutes.editPolicy}?id=${userId}`);
  };

  return (
    <div className="">
      <h1 className="tex-2xl sm:text-[32px] text-primaryText font-bold mb-6">
        Policy & Agreement
      </h1>
      <div className="flex justify-end items-center gap-5 my-6">
        <div className="w-full sm:w-[388px] relative">
          <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-primaryText text-lg" />
          <input
            type="search"
            placeholder="Search by Policy Title"
            value={filters.heading}
            onChange={handleChangeFilter}
            onKeyUp={handleSearch}
            name="heading"
            className="w-full pl-10 p-2 border-[0.6px] border-borderLightGray rounded-full focus:outline-none bg-coolLightGrayBg"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
        {data.map((item: any, index: number) => (
          <div
            key={index}
            className="p-3 bg-white rounded-xl border border-borderColor"
          >
            <div className="relative">
              <span className="flex justify-center items-center my-2">
                <DocumentSvg className="w-32 h-20" />
              </span>
              <Fragment>
                {hasPermission(sitePermissions.policyAgreementUpdate) ? (
                  <div
                    onClick={() => {
                      navigate(`${siteRoutes.editPolicy}?id=${item.id}`);
                    }}
                    className="bg-goldenOrangeBg absolute right-0 top-0 w-fit p-2 rounded-lg cursor-pointer"
                  >
                    <EditIcon className="w-6" />
                  </div>
                ) : (
                  <div className="bg-goldenOrangeBg absolute right-0 top-0 w-fit p-2 rounded-lg cursor-not-allowed">
                    <EditIcon className="w-6" />
                  </div>
                )}
              </Fragment>
            </div>
            <div className="py-2">
              <h3 className="text-lg font-semibold text-primaryText">
                {item.heading?.split(" ").slice(0, 8).join(" ") +
                  (item.heading?.split(" ").length > 10 ? "..." : "")}
              </h3>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PolicyAgreement;
